"use client";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { ICanvas, IChat, IConfig, IMessage, IProject, IUser } from "@/lib/types";
import {
    ChatsContext,
    UserContext,
    ConfigContext,
    ProjectsContext,
    ErrorContext,
    StreamingContext,
    IApiError,
} from "./context";
import { createStreamManager, type StreamManager } from "@/hooks/useStreamManager";
import { SessionCallbacks } from "@/lib/streamSession";
import {
    CHATS_PER_PAGE,
    getChats,
    getProjects,
    loadConfig,
    saveConfig,
    updateChat as daoUpdateChat,
    deleteChat as daoDeleteChat,
    createProject as daoCreateProject,
    updateProject as daoUpdateProject,
    deleteProject as daoDeleteProject,
} from "@/lib/dao";
import { getJWT, isInteractionRequired } from "@/lib/auth";
import { buildSessionExpiredError } from "@/hooks/useSessionExpiry";
import { setBugsnagUser } from "@/lib/bugsnag";
import { HEALTH_API_URL } from "@/lib/utils";

function isNetworkOrCorsError(message?: string): boolean {
    if (!message) return false;
    return (
        message.includes("Failed to fetch") ||
        message.includes("Load failed") ||
        message.includes("NetworkError") ||
        message.includes("CORS")
    );
}

// Merge server chats with local state. The server is authoritative for any chatid
// it returns; chats present locally but absent from the server response are
// optimistic/not-yet-persisted and must be preserved (e.g. a brand-new chat created
// on the home page whose stream request hasn't persisted it server-side yet).
// Without this, a getChats load that resolves after the optimistic insert would
// wipe the new chat and bounce the user out of the conversation they just started.
export function mergeChats(local: IChat[], server: IChat[]): IChat[] {
    const serverIds = new Set(server.map((c) => c.chatid));
    const localOnly = local.filter((c) => !serverIds.has(c.chatid));
    return [...server, ...localOnly].sort((a, b) => b.lastmessage.getTime() - a.lastmessage.getTime());
}

export const Providers = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = React.useState<IUser | undefined>(undefined);
    const [chats, setChats] = React.useState<IChat[]>([]);
    const chatsRef = useRef(chats);
    chatsRef.current = chats;
    const [projects, setProjects] = React.useState<IProject[]>([]);
    const [config, setConfig] = React.useState<IConfig>(loadConfig());
    const [errors, setErrors] = React.useState<IApiError[]>([]);
    const networkWarningRef = useRef(false);
    const errorCounterRef = useRef(0);
    // Tracks which account we've already loaded chats/projects for, so the load
    // effect doesn't re-fetch (and re-replace state) when MSAL hands back a new
    // `accounts` array reference on token refresh.
    const loadedForAccountRef = useRef<string | null>(null);

    const { instance, accounts } = useMsal();

    const addError = useCallback((error: IApiError) => {
        console.error(`[API Error] ${error.type}:`, error.message, error.details || "");
        setErrors((prev) => {
            // At most one session banner. Effects keyed on the MSAL `accounts`
            // identity re-run on every refresh attempt, and the banner stack has
            // no max-height — duplicates would bury the "Sign in again" button
            // that is the user's only way out.
            if (error.type === "session" && prev.some((e) => e.type === "session")) return prev;
            return [...prev, error];
        });
        // Auto-dismiss after 10 seconds. Warnings persist until manually closed,
        // and so must session errors: dismissing one would leave a dead app with
        // no affordance to recover it.
        if (error.type !== "warning" && error.type !== "session") {
            setTimeout(() => {
                setErrors((prev) => prev.filter((e) => e.timestamp !== error.timestamp));
            }, 10000);
        }
    }, []);

    const clearError = useCallback((timestamp: number) => {
        setErrors((prev) => prev.filter((e) => e.timestamp !== timestamp));
    }, []);

    const clearAllErrors = useCallback(() => {
        setErrors([]);
    }, []);

    const errorContextValue = useMemo(
        () => ({
            errors,
            addError,
            clearError,
            clearAllErrors,
        }),
        [errors, addError, clearError, clearAllErrors]
    );

    const updateUser = useCallback((inputUser: IUser | undefined) => {
        setUser(inputUser);
    }, []);

    const userContextValue = useMemo(
        () => ({
            user: user,
            updateUser: updateUser,
        }),
        [user, updateUser]
    );

    const updateChat = useCallback(
        async (inputChat: Partial<IChat>, doNetwork: boolean, doUpdateLastmessage = false) => {
            if (doUpdateLastmessage) {
                inputChat.lastmessage = new Date();
            }
            setChats((prev) => {
                const existingChat = prev.find((chat) => chat.chatid === inputChat.chatid);
                if (existingChat) {
                    console.log(`${Date.now()} existingChat in updateChat: ${JSON.stringify(existingChat)}`);
                    const updatedChat = { ...existingChat, ...inputChat };
                    return prev.map((chat) => (chat.chatid === inputChat.chatid ? updatedChat : chat));
                } else {
                    return [...prev, inputChat as IChat].sort(
                        (a, b) => b.lastmessage.getTime() - a.lastmessage.getTime()
                    );
                }
            });
            if (doNetwork && inputChat.chatid) {
                getJWT(instance, accounts)
                    .then((jwt) => daoUpdateChat(jwt, inputChat as Partial<IChat> & Pick<IChat, "chatid">))
                    .catch((error) => {
                        addError({
                            type: "api",
                            message: `Failed to save chat changes: ${error.message}`,
                            details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                            timestamp: Date.now() + ++errorCounterRef.current,
                        });
                    });
            }
        },
        [accounts, instance, addError]
    );

    const deleteChat = useCallback(
        async (chatid: string) => {
            setChats((prev) => prev.filter((chat) => chat.chatid !== chatid));
            getJWT(instance, accounts)
                .then((jwt) => daoDeleteChat(jwt, chatid))
                .catch((error) => {
                    addError({
                        type: "api",
                        message: `Failed to delete chat: ${error.message}`,
                        details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                        timestamp: Date.now() + ++errorCounterRef.current,
                    });
                });
        },
        [accounts, instance, addError]
    );

    const chatsContextValue = useMemo(
        () => ({
            chats: chats,
            setChats: setChats,
            updateChat: updateChat,
            deleteChat: deleteChat,
        }),
        [chats, updateChat, deleteChat]
    );

    const createProject = useCallback(
        async (inputProject: Pick<IProject, "title" | "description">): Promise<IProject> => {
            const jwt = await getJWT(instance, accounts);
            const createdProject = await daoCreateProject(jwt, inputProject);
            setProjects((prev) => [...prev, createdProject]);
            return createdProject;
        },
        [accounts, instance]
    );

    const updateProject = useCallback(
        async (inputProject: Partial<IProject>) => {
            setProjects((prev) => {
                const existingProject = prev.find((project) => project.projectid === inputProject.projectid);
                if (existingProject) {
                    const updatedProject = { ...existingProject, ...inputProject };
                    return prev.map((project) =>
                        project.projectid === inputProject.projectid ? updatedProject : project
                    );
                }
                return [...prev, inputProject as IProject].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
            });
            getJWT(instance, accounts)
                .then((jwt) => daoUpdateProject(jwt, inputProject as Partial<IProject> & Pick<IProject, "projectid">))
                .catch((error) => {
                    addError({
                        type: "api",
                        message: `Failed to save project changes: ${error.message}`,
                        details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                        timestamp: Date.now() + ++errorCounterRef.current,
                    });
                });
        },
        [accounts, instance, addError]
    );

    const deleteProject = useCallback(
        async (projectid: string) => {
            setProjects((prev) => prev.filter((project) => project.projectid !== projectid));
            // Remove chats from this project from the navbar list
            setChats((prev) => prev.filter((chat) => chat.projectid !== projectid));
            getJWT(instance, accounts)
                .then((jwt) => daoDeleteProject(jwt, projectid))
                .catch((error) => {
                    addError({
                        type: "api",
                        message: `Failed to delete project: ${error.message}`,
                        details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                        timestamp: Date.now() + ++errorCounterRef.current,
                    });
                });
        },
        [accounts, instance, addError]
    );

    const projectsContextValue = useMemo(
        () => ({
            projects: projects,
            setProjects: setProjects,
            createProject: createProject,
            updateProject: updateProject,
            deleteProject: deleteProject,
        }),
        [projects, createProject, updateProject, deleteProject]
    );

    const updateConfig = (config: IConfig) => {
        setConfig(config);
        saveConfig(config);
    };

    const configContextValue = {
        config: config,
        updateConfig: updateConfig,
    };

    // ─── Streaming manager (concurrent per-chat streams) ─────────────────
    // The chat the user is currently viewing — used to gate canvas auto-open so
    // a background chat finishing a document doesn't pop the canvas over the
    // chat in view.
    const viewedChatIdRef = useRef<string | null>(null);
    // Latest config/updateConfig, read inside the canvas callback without making
    // the stream callbacks (or the manager) depend on them.
    const configRef = useRef(config);
    configRef.current = config;
    const updateConfigRef = useRef(updateConfig);
    updateConfigRef.current = updateConfig;

    // Stream callbacks rebuilt each render and read by sessions via a getter, so
    // long-lived background sessions always see the latest selectedModel/closures
    // without being recreated.
    const streamCallbacks: SessionCallbacks = {
        getJwt: () => getJWT(instance, accounts),
        onMessagesCommitted: (chatid: string, messages: IMessage[]) => {
            setChats((prev) => prev.map((c) => (c.chatid === chatid ? { ...c, messages } : c)));
        },
        getChatTitle: (chatid: string) => chatsRef.current.find((c) => c.chatid === chatid)?.title,
        onTitleGenerated: (chatid: string, title: string) => {
            // Never overwrite a title the user already set (e.g. rename mid-stream).
            setChats((prev) =>
                prev.map((c) => {
                    if (c.chatid !== chatid) return c;
                    if (c.title !== undefined && c.title.trim() !== "") return c;
                    return { ...c, title };
                })
            );
        },
        onCanvasOpen: (chatid: string, canvas: ICanvas) => {
            // Only open the canvas if this chat is the one in view; a background
            // chat creating a document must not hijack the current canvas.
            if (chatid !== viewedChatIdRef.current) return;
            updateConfigRef.current({ ...configRef.current, canvasOpen: true, navbarOpen: false, canvas });
        },
        onSessionExpired: (error: unknown) => {
            if (!isInteractionRequired(error)) return false;
            addError(buildSessionExpiredError(instance, accounts));
            return true;
        },
        selectedModel: config.selectedModel,
    };
    const streamCallbacksRef = useRef(streamCallbacks);
    streamCallbacksRef.current = streamCallbacks;

    // Manager created once; it reads the latest callbacks through the ref.
    const managerRef = useRef<StreamManager | null>(null);
    if (managerRef.current === null) {
        managerRef.current = createStreamManager(() => streamCallbacksRef.current);
    }
    const manager = managerRef.current;

    // Tear down all sessions only when the whole app unmounts (Layer 1 means the
    // backend still persists each full result, so aborting here loses nothing).
    useEffect(() => {
        return () => {
            manager.destroyAll();
        };
    }, [manager]);

    const streamingContextValue = useMemo(
        () => ({
            startStream: manager.startStream,
            stopStream: manager.stopStream,
            setLoadingState: manager.setLoadingState,
            setViewedChatId: (chatid: string | null) => {
                viewedChatIdRef.current = chatid;
            },
            _manager: manager,
        }),
        [manager]
    );

    // Dev-only health check: detect when API is unreachable (e.g. not on VPN)
    useEffect(() => {
        if (process.env.NEXT_PUBLIC_ENV !== "development") return;

        let cancelled = false;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        fetch(HEALTH_API_URL, { signal: controller.signal })
            .then((res) => {
                if (!cancelled && !res.ok) {
                    networkWarningRef.current = true;
                    addError({
                        type: "warning",
                        message:
                            "You need to be connected to a Fender network for FAI to function properly. Please connect to the Fender VPN or office WiFi and try again.",
                        timestamp: Date.now(),
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    networkWarningRef.current = true;
                    addError({
                        type: "warning",
                        message:
                            "You need to be connected to a Fender network for FAI to function properly. Please connect to the Fender VPN or office WiFi and try again.",
                        timestamp: Date.now(),
                    });
                }
            })
            .finally(() => {
                clearTimeout(timeout);
            });

        return () => {
            cancelled = true;
            controller.abort();
            clearTimeout(timeout);
        };
    }, [addError]);

    useEffect(() => {
        // Set the user from MSAL
        if (accounts.length === 0) {
            setUser(undefined);
            // Reset so a subsequent sign-in reloads chats/projects fresh.
            loadedForAccountRef.current = null;
        } else {
            setUser({
                userid: accounts[0].homeAccountId,
                name: accounts[0].name || "User",
                email: accounts[0].username,
                department: accounts[0].idTokenClaims?.department as string | undefined,
            });
            setBugsnagUser({
                id: accounts[0].homeAccountId,
                email: accounts[0].username,
                name: accounts[0].name,
            });
        }
        // Load the config from localstorage
        // We don't have to do this, it's done at the initial provider level
        setConfig(loadConfig());
        // Load the chats and projects from the network
        if (accounts.length > 0) {
            // Only load once per signed-in account. MSAL hands back a new `accounts`
            // array reference on token refresh, which re-runs this effect; without
            // this guard the re-fetch would replace state and could wipe an
            // optimistic, not-yet-persisted chat the user just created.
            const accountId = accounts[0].homeAccountId;
            if (loadedForAccountRef.current === accountId) return;
            loadedForAccountRef.current = accountId;

            getJWT(instance, accounts)
                .then((jwt) => {
                    console.log("[Providers] Got JWT, fetching chats and projects");

                    getChats(jwt, 0, CHATS_PER_PAGE)
                        .then((serverChats) => {
                            console.log("[Providers] Successfully loaded chats:", serverChats.length);
                            setChats((prev) => mergeChats(prev, serverChats));
                        })
                        .catch((error) => {
                            console.error("[Providers] Failed to load chats:", error);
                            const networkError = isNetworkOrCorsError(error.message);

                            // Suppress redundant network errors when network warning is already shown
                            if (networkError && networkWarningRef.current) return;

                            addError({
                                type: networkError ? "network" : "api",
                                message: networkError
                                    ? "Cannot reach the API server"
                                    : `Failed to load chats: ${error.message}`,
                                details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                                timestamp: Date.now() + ++errorCounterRef.current,
                            });
                        });

                    getProjects(jwt)
                        .then((projects) => {
                            console.log("[Providers] Successfully loaded projects:", projects.length);
                            setProjects(projects);
                        })
                        .catch((error) => {
                            console.error("[Providers] Failed to load projects:", error);
                            const networkError = isNetworkOrCorsError(error.message);

                            // Suppress redundant network errors when network warning is already shown
                            if (networkError && networkWarningRef.current) return;

                            addError({
                                type: networkError ? "network" : "api",
                                message: networkError
                                    ? "Cannot reach the API server"
                                    : `Failed to load projects: ${error.message}`,
                                details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                                timestamp: Date.now() + ++errorCounterRef.current,
                            });
                        });
                })
                .catch((error) => {
                    console.error("[Providers] Failed to get JWT:", error);
                    // Allow a retry once the user signs in again: this ref is what
                    // stops the effect re-running, and the redirect that used to
                    // reset it by reloading the page is gone.
                    loadedForAccountRef.current = null;
                    if (isInteractionRequired(error)) {
                        addError(buildSessionExpiredError(instance, accounts));
                        return;
                    }
                    addError({
                        type: "auth",
                        message: "Failed to get authentication token",
                        details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                        timestamp: Date.now(),
                    });
                });
        }
    }, [accounts, instance, addError]);

    return (
        <>
            <ErrorContext value={errorContextValue}>
                <UserContext value={userContextValue}>
                    <ProjectsContext value={projectsContextValue}>
                        <ChatsContext value={chatsContextValue}>
                            <StreamingContext value={streamingContextValue}>
                                <ConfigContext value={configContextValue}>{children}</ConfigContext>
                            </StreamingContext>
                        </ChatsContext>
                    </ProjectsContext>
                </UserContext>
            </ErrorContext>
        </>
    );
};
